/**
 * DataContext — compatibility facade over the focused data providers.
 *
 * The Firestore listeners that used to live here are split into slice
 * providers under ./data/ (rules, transactions, accounts, targeting, credit
 * score, daily wellness), each owning its own listeners and state. This file
 * composes them and republishes the merged value through the SAME DataContext
 * + useData() shape, so existing consumers (and DemoProviders, which stubs
 * DataContext.Provider directly) keep working unchanged.
 *
 * New code should still consume useData() until consumers are migrated to the
 * slice hooks in follow-ups (demo mode only stubs this facade today).
 */
import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { Account, Transaction, TransactionRule, UnifiedAccount, AccountMatchRecord, GrowTargetingCache, RecurringTransaction, SpinwheelCreditScore, PayerInfo } from '@shared/types';
import { TransactionRulesProvider, useTransactionRulesContext } from './data/TransactionRulesProvider';
import { TransactionsProvider, useTransactionsContext } from './data/TransactionsProvider';
import { AccountsProvider, useAccountsContext, ItemConnectionStatus } from './data/AccountsProvider';
import { TargetingProvider, useTargetingContext } from './data/TargetingProvider';
import { CreditScoreProvider, useCreditScoreContext } from './data/CreditScoreProvider';
import { DailyWellnessProvider, useDailyWellnessContext } from './data/DailyWellnessProvider';

export type { ItemConnectionStatus };

interface DataContextType {
    plaidAssets: Account[]; // Plaid asset accounts (checking/savings)
    unifiedLiabilities: UnifiedAccount[]; // Merged liability accounts (spinwheelUsers + plaidLiabilityAccounts → unifiedAccounts)
    pendingMatches: AccountMatchRecord[]; // Matches needing user review
    transactions: Transaction[];
    transactionRules: TransactionRule[];
    dailyWellnessMessage: string;
    loadingData: boolean;
    hasPendingConnections: boolean; // Whether user has unmatched Plaid accounts
    recurringTransactions: RecurringTransaction[];
    growTargetingCache: GrowTargetingCache | null; // Eligible video/exercise IDs (computed once per UTC day)
    spinwheelCreditScore: SpinwheelCreditScore | null;
    payerInfo: PayerInfo;
    itemConnectionStatuses: Map<string, ItemConnectionStatus>; // item_id → connection health
    monthlyIncomeOverride: number | null; // user-set monthly income (uservars), null = use estimate
}

/** @internal Exported for demo mode — do not use directly in app components */
export const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};

/** Reads every slice context and republishes the merged legacy value. */
const DataFacade: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { transactionRules } = useTransactionRulesContext();
    const { transactions, recurringTransactions, loadingData } = useTransactionsContext();
    const {
        plaidAssets,
        unifiedLiabilities,
        pendingMatches,
        hasPendingConnections,
        itemConnectionStatuses,
        payerInfo,
        monthlyIncomeOverride,
    } = useAccountsContext();
    const { growTargetingCache } = useTargetingContext();
    const { spinwheelCreditScore } = useCreditScoreContext();
    const { dailyWellnessMessage } = useDailyWellnessContext();

    const value = useMemo(() => ({
        plaidAssets,
        unifiedLiabilities,
        pendingMatches,
        transactions,
        transactionRules,
        recurringTransactions,
        dailyWellnessMessage,
        loadingData,
        hasPendingConnections,
        growTargetingCache,
        spinwheelCreditScore,
        payerInfo,
        itemConnectionStatuses,
        monthlyIncomeOverride,
    }), [
        plaidAssets,
        unifiedLiabilities,
        pendingMatches,
        transactions,
        transactionRules,
        recurringTransactions,
        dailyWellnessMessage,
        loadingData,
        hasPendingConnections,
        growTargetingCache,
        spinwheelCreditScore,
        payerInfo,
        itemConnectionStatuses,
        monthlyIncomeOverride,
    ]);

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
    <TransactionRulesProvider>
        <TransactionsProvider>
            <AccountsProvider>
                <TargetingProvider>
                    <CreditScoreProvider>
                        <DailyWellnessProvider>
                            <DataFacade>{children}</DataFacade>
                        </DailyWellnessProvider>
                    </CreditScoreProvider>
                </TargetingProvider>
            </AccountsProvider>
        </TransactionsProvider>
    </TransactionRulesProvider>
);
